/**
 * CheckingProductionEntryRegister · P17-UAT-17
 * READ-ONLY register for Checking Production Entries (production_entries · stage=Checking).
 * Authoritative source: GET /checking-production-entries. Viewing causes ZERO V2 mutation.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { exportToCSV } from "@/lib/csv";
import { MagnifyingGlassIcon, ArrowsClockwiseIcon, DownloadSimpleIcon, XIcon, PencilSimpleIcon, CheckCircleIcon } from "@phosphor-icons/react";
import { txnBtn, txnBtnSize } from "@/components/txn";
import { RowActions } from "@/components/RowActions";

const ACCENT = "#38bdf8";
const TID = "checking-register";
const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 }));
// REV-A · derive metres for display (prefer the frozen persisted snapshot, else derive from frozen bom_standard_gpm). UI/presentation only.
const mOf = (kg, gpm) => (gpm && Number(gpm) > 0 ? Number(kg || 0) * 1000 / Number(gpm) : null);
const metresVal = (persisted, kg, gpm) => (persisted !== null && persisted !== undefined && persisted !== "" ? Number(persisted) : mOf(kg, gpm));
const fmM = (persisted, kg, gpm) => { const m = metresVal(persisted, kg, gpm); return m === null || m === undefined ? "— m" : `${fmt(m)} m`; };
const fmMcell = (persisted, kg, gpm) => { const m = metresVal(persisted, kg, gpm); return m === null || m === undefined ? "—" : fmt(m); };  // REV-B · register column: numeric only (header carries UOM)
const pctOf = (v) => (v === null || v === undefined || v === "" ? "—" : `${Number(v).toFixed(2)}%`);
const dtOf = (iso) => { if (!iso) return "—"; const d = new Date(iso); return isNaN(d) ? "—" : d.toLocaleString([], { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }); };
const S = { POSTED: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", DRAFT: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40", REVERSED: "bg-rose-500/15 text-rose-300 border-rose-500/40" };
const Badge = ({ text, tid }) => <span data-testid={tid} className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold ${S[text] || "bg-zinc-700 text-zinc-200 border-zinc-600"}`}>{text || "—"}</span>;

export default function CheckingProductionEntryRegister() {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canEdit = hasPerm ? hasPerm("inventory:edit") : true;
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [viewRow, setViewRow] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setRows(await api.get("/checking-production-entries").then(r => r.data || [])); }
    catch { toast.error("Failed to load register"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r => [r.entry_no, r.wo_no, r.so_eso, r.product_code, r.source_batch, r.status].some(x => (x || "").toString().toLowerCase().includes(t)));
  }, [rows, q]);

  const doReverse = async (row) => {
    const reason = window.prompt(`Reverse ${row.entry_no}? Enter reason (min 4 chars):`);
    if (!reason || reason.trim().length < 4) return;
    try { await api.post(`/production-entries/${row.id}/checking-reverse`, { reason: reason.trim() }); toast.success(`${row.entry_no} reversed`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reverse failed"); }
  };

  const exportCsv = () => exportToCSV(filtered.map(r => ({
    CPPE: r.entry_no, Date: r.entry_date, WO: r.wo_no, SOESO: r.so_eso, Product: r.product_code,
    "Source Godown": r.source_godown, "Source Batch": r.source_batch, "BOM Standard g/m": r.bom_standard_gpm,
    "Total Checked KG": r.total_checked_kg, "Total Checked M": metresVal(r.total_checked_m, r.total_checked_kg, r.bom_standard_gpm),
    "First Quality KG": r.first_quality_kg, "First Quality M": metresVal(r.first_quality_m, r.first_quality_kg, r.bom_standard_gpm),
    "Seconds KG": r.second_quality_kg, "Seconds M": metresVal(r.second_quality_m, r.second_quality_kg, r.bom_standard_gpm),
    "Waste KG": r.wastage_kg, "Waste M": metresVal(r.wastage_m, r.wastage_kg, r.bom_standard_gpm),
    "First %": r.first_quality_pct, "Seconds %": r.second_quality_pct, "Waste %": r.wastage_pct,
    Status: r.status, V2: r.v2_posting_status, CreatedBy: r.created_by,
  })), "checking_production_register.csv");

  return (
    <div className="p-6" data-testid={`${TID}-page`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-black text-zinc-100">Checking Production Entry Register</h1>
          <p className="text-sm text-zinc-500">Read-only control surface · WO authoritative · SO/ESO reference</p>
        </div>
        <div className="flex gap-2">
          <button data-testid={`${TID}-new`} onClick={() => navigate("/production/checking-entry")} className="h-9 px-4 rounded-lg text-sm font-bold text-zinc-950" style={{ background: ACCENT }}>+ New Entry</button>
          <button data-testid={`${TID}-export`} onClick={exportCsv} className="h-9 px-3 rounded-lg text-sm border border-zinc-700 text-zinc-200 inline-flex items-center gap-1"><DownloadSimpleIcon size={16} />CSV</button>
          <button data-testid={`${TID}-refresh`} onClick={load} className="h-9 px-3 rounded-lg text-sm border border-zinc-700 text-zinc-200 inline-flex items-center gap-1"><ArrowsClockwiseIcon size={16} /></button>
        </div>
      </div>
      <div className="relative mb-3 max-w-sm">
        <MagnifyingGlassIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input data-testid={`${TID}-search`} value={q} onChange={e => setQ(e.target.value)} placeholder="Search CPPE / WO / product / batch" className="w-full h-9 pl-9 pr-3 bg-zinc-950 border border-zinc-800 rounded-md text-sm text-zinc-100 outline-none focus:border-sky-500" />
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-800">
        <table className="min-w-full text-xs">
          <thead className="bg-zinc-900/60 text-zinc-400">
            <tr>{["CPPE", "Date", "WO", "SO/ESO", "Product", "Src Godown", "Src Batch", "Total KG", "Total M", "First KG", "First M", "Seconds KG", "Seconds M", "Waste KG", "Waste M", "First%", "Seconds%", "Waste%", "Status", "V2", "Action"].map(h => <th key={h} className="px-2.5 py-2 text-left font-semibold whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={21} className="px-3 py-8 text-center text-zinc-500">{loading ? "Loading…" : "No Checking entries yet"}</td></tr>}
            {filtered.map(r => (
              <tr key={r.id} data-testid={`${TID}-row-${r.entry_no}`} className="border-t border-zinc-800/70 hover:bg-zinc-900/40">
                <td className="px-2.5 py-2 font-mono text-sky-300 whitespace-nowrap">{r.entry_no}</td>
                <td className="px-2.5 py-2 whitespace-nowrap">{r.entry_date || "—"}</td>
                <td className="px-2.5 py-2 whitespace-nowrap">{r.wo_no || "—"}</td>
                <td className="px-2.5 py-2 whitespace-nowrap text-zinc-400">{r.so_eso || "—"}</td>
                <td className="px-2.5 py-2 font-mono whitespace-nowrap">{r.product_code || "—"}</td>
                <td className="px-2.5 py-2 whitespace-nowrap">{r.source_godown || "—"}</td>
                <td className="px-2.5 py-2 font-mono whitespace-nowrap">{r.source_batch || "—"}</td>
                <td className="px-2.5 py-2 text-right" data-testid={`${TID}-total-kg-${r.entry_no}`}>{fmt(r.total_checked_kg)}</td>
                <td className="px-2.5 py-2 text-right text-zinc-400" data-testid={`${TID}-total-m-${r.entry_no}`}>{fmMcell(r.total_checked_m, r.total_checked_kg, r.bom_standard_gpm)}</td>
                <td className="px-2.5 py-2 text-right text-sky-300" data-testid={`${TID}-first-kg-${r.entry_no}`}>{fmt(r.first_quality_kg)}</td>
                <td className="px-2.5 py-2 text-right text-zinc-400" data-testid={`${TID}-first-m-${r.entry_no}`}>{fmMcell(r.first_quality_m, r.first_quality_kg, r.bom_standard_gpm)}</td>
                <td className="px-2.5 py-2 text-right text-amber-300" data-testid={`${TID}-second-kg-${r.entry_no}`}>{fmt(r.second_quality_kg)}</td>
                <td className="px-2.5 py-2 text-right text-zinc-400" data-testid={`${TID}-second-m-${r.entry_no}`}>{fmMcell(r.second_quality_m, r.second_quality_kg, r.bom_standard_gpm)}</td>
                <td className="px-2.5 py-2 text-right text-rose-300" data-testid={`${TID}-waste-kg-${r.entry_no}`}>{fmt(r.wastage_kg)}</td>
                <td className="px-2.5 py-2 text-right text-zinc-400" data-testid={`${TID}-waste-m-${r.entry_no}`}>{fmMcell(r.wastage_m, r.wastage_kg, r.bom_standard_gpm)}</td>
                <td className="px-2.5 py-2 text-right">{pctOf(r.first_quality_pct)}</td>
                <td className="px-2.5 py-2 text-right">{pctOf(r.second_quality_pct)}</td>
                <td className="px-2.5 py-2 text-right">{pctOf(r.wastage_pct)}</td>
                <td className="px-2.5 py-2"><Badge text={r.status} tid={`${TID}-status-${r.entry_no}`} /></td>
                <td className="px-2.5 py-2"><Badge text={r.v2_posting_status} /></td>
                <td className="px-2.5 py-2 whitespace-nowrap">
                  <RowActions
                    testid={`${TID}-actions-${r.entry_no}`}
                    items={[
                      { label: "View", testid: `${TID}-view-${r.entry_no}`, onClick: () => setViewRow(r) },
                      { label: "Edit Draft", testid: `${TID}-edit-${r.entry_no}`, show: r.status !== "POSTED" && r.status !== "REVERSED" && canEdit, onClick: () => navigate(`/production/checking-entry/${r.id}`) },
                      { label: "Reverse", tone: "reverse", testid: `${TID}-reverse-${r.entry_no}`, show: r.status === "POSTED" && canEdit, onClick: () => doReverse(r) },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {viewRow && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-6 overflow-auto" onClick={() => setViewRow(null)}>
          <div className="w-full max-w-4xl rounded-2xl border border-zinc-700 bg-zinc-950 p-6" onClick={e => e.stopPropagation()} data-testid={`${TID}-view-modal`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{viewRow.status === "POSTED" ? "Checking Production Entry · Frozen Snapshot" : "Checking Production Entry"}</div>
                <div className="text-xl font-black text-zinc-100">{viewRow.entry_no}</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge text={viewRow.status} /><Badge text={viewRow.v2_posting_status} />
                <button onClick={() => setViewRow(null)} className="p-1 text-zinc-400 hover:text-zinc-100"><XIcon size={18} /></button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              {[
                ["Date", viewRow.entry_date], ["WO (Authority)", viewRow.wo_no], ["SO/ESO (Reference)", viewRow.so_eso], ["Product", viewRow.product_code],
                ["Source Godown", viewRow.source_godown], ["Source Batch", viewRow.source_batch],
                ["BOM Standard g/m", viewRow.bom_standard_gpm ? `${fmt(viewRow.bom_standard_gpm)} g/m` : "—"], ["Generation", viewRow.generation],
                ["Total Checked (kg)", `${fmt(viewRow.total_checked_kg)} kg`], ["Total Checked (m)", fmM(viewRow.total_checked_m, viewRow.total_checked_kg, viewRow.bom_standard_gpm)],
                ["First Quality (kg)", `${fmt(viewRow.first_quality_kg)} kg`], ["First Quality (m)", fmM(viewRow.first_quality_m, viewRow.first_quality_kg, viewRow.bom_standard_gpm)],
                ["Seconds (kg)", `${fmt(viewRow.second_quality_kg)} kg`], ["Seconds (m)", fmM(viewRow.second_quality_m, viewRow.second_quality_kg, viewRow.bom_standard_gpm)],
                ["Waste (kg)", `${fmt(viewRow.wastage_kg)} kg`], ["Waste (m)", fmM(viewRow.wastage_m, viewRow.wastage_kg, viewRow.bom_standard_gpm)],
                ["First %", pctOf(viewRow.first_quality_pct)], ["Seconds %", pctOf(viewRow.second_quality_pct)], ["Waste %", pctOf(viewRow.wastage_pct)],
                ["First Output Batch", viewRow.first_output_batch], ["Seconds Output Batch", viewRow.seconds_output_batch],
                ["Created By", viewRow.created_by], ["Posted By", viewRow.posted_by],
                ["Posted At", dtOf(viewRow.posted_at)], ["Product (both dispositions)", viewRow.product_code],
              ].map(([k, v]) => (
                <div key={k}><div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div><div className="text-sm font-semibold text-zinc-100 break-words">{v ?? "—"}</div></div>
              ))}
            </div>
            {viewRow.status === "REVERSED" && (
              <div className="mt-4 rounded-lg border border-rose-700/40 bg-rose-950/30 p-3 text-sm text-rose-300">
                Reversed by {viewRow.reversed_by || "—"} · {dtOf(viewRow.reversed_at)}{viewRow.reversal_reason ? ` · ${viewRow.reversal_reason}` : ""}
              </div>
            )}
            <div className="mt-6 flex items-center justify-between gap-3 border-t border-zinc-800 pt-4" data-testid={`${TID}-view-actions`}>
              <button data-testid={`${TID}-view-close`} onClick={() => setViewRow(null)} className={`${txnBtn.secondary} ${txnBtnSize.standard}`}>Close</button>
              {viewRow.status !== "POSTED" && viewRow.status !== "REVERSED" && canEdit && (
                <div className="flex items-center gap-2">
                  <button data-testid={`${TID}-view-edit`} onClick={() => navigate(`/production/checking-entry/${viewRow.id}`)} className={`${txnBtn.secondary} ${txnBtnSize.standard}`}>
                    <PencilSimpleIcon size={15} weight="bold" /> Edit
                  </button>
                  <button data-testid={`${TID}-view-review`} onClick={() => navigate(`/production/checking-entry/${viewRow.id}?review=1`)} className={`${txnBtn.primary} ${txnBtnSize.wide}`}>
                    <CheckCircleIcon size={15} weight="bold" /> Review &amp; Post
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
